import React, { useState } from 'react';
import { ShoppingCart, Search, Trash2, Plus, Minus, Printer, X, CreditCard, Banknote, QrCode } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function CounterPOS({ 
  currentTenant, 
  products = [], 
  customers = [], 
  gstEnabled = true, 
  lang = 'mr' 
}) {
  const isMr = lang === 'mr';
  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [cashTendered, setCashTendered] = useState('');
  const [printModalBill, setPrintModalBill] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter products by name, code, barcode, or system QR
  const filteredProducts = products.filter(p => {
    const s = search.toLowerCase();
    const nameStr = (p.name_mr || p.name || p.name_en || '').toLowerCase();
    const codeStr = (p.code || '').toLowerCase();
    const barStr = (p.barcode || '').toLowerCase();
    const qrStr = (p.system_qr_code || p.systemQrCode || '').toLowerCase();
    return nameStr.includes(s) || codeStr.includes(s) || barStr.includes(s) || qrStr.includes(s);
  });

  const creditCustomers = customers.filter(c => c.is_credit_customer !== false && c.isCreditCustomer !== false);

  const addToCart = (product) => {
    const rate = parseFloat(product.retail_rate !== undefined ? product.retail_rate : (product.retailRate || 0));
    const name = product.name_mr || product.name || product.name_en || '';
    const unit = product.unit_mr || product.unit || 'नग';
    const cgst = parseFloat(product.cgst_pct || product.cgst || 0);
    const sgst = parseFloat(product.sgst_pct || product.sgst || 0);

    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
      }
      return [...prev, {
        id: product.id,
        code: product.code,
        name,
        name_mr: name,
        rate,
        unit,
        cgst,
        sgst,
        qty: 1
      }];
    });
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(item => item.id === id ? { ...item, qty: Math.max(1, item.qty + delta) } : item));
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.rate * item.qty), 0);
  const taxAmount = gstEnabled ? cart.reduce((sum, item) => {
    const taxPct = (item.cgst || 0) + (item.sgst || 0);
    return sum + (item.rate * item.qty * (taxPct / 100));
  }, 0) : 0;
  const grandTotal = Math.round(subtotal + taxAmount);
  const changeDue = cashTendered ? Math.max(0, parseFloat(cashTendered) - grandTotal) : 0;

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (paymentMode === 'CREDIT' && !selectedCustomerId) {
      alert(isMr ? 'कृपया उधारी ग्राहकाचे नाव निवडा!' : 'Please select a credit customer!');
      return;
    }

    setIsSubmitting(true);
    const custObj = creditCustomers.find(c => c.id.toString() === selectedCustomerId.toString());
    const custName = paymentMode === 'CREDIT' ? (custObj?.name_mr || custObj?.name || 'खातेदार') : (isMr ? 'काऊंटर रोख ग्राहक' : 'Counter Cash Customer');

    const billPayload = {
      customer_id: paymentMode === 'CREDIT' ? selectedCustomerId : null,
      customer_name: custName,
      payment_mode: paymentMode,
      subtotal,
      tax_amount: taxAmount,
      grand_total: grandTotal,
      cash_tendered: cashTendered ? parseFloat(cashTendered) : grandTotal,
      change_due: changeDue,
      items: cart.map(i => ({
        id: i.id,
        name_mr: i.name,
        qty: i.qty,
        rate: i.rate,
        tax: gstEnabled ? (i.rate * i.qty * ((i.cgst + i.sgst) / 100)) : 0,
        total: Math.round(i.rate * i.qty)
      }))
    };

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/pos/bills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(billPayload)
      });
      const data = await res.json();

      const realBillNo = data.bill_no || ('BILL-' + Date.now().toString().slice(-6));

      const billModalData = {
        billNo: realBillNo,
        date: new Date().toLocaleDateString('en-IN'),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cart: [...cart],
        subtotal,
        taxAmount,
        grandTotal,
        paymentMode,
        cashTendered: cashTendered ? parseFloat(cashTendered) : grandTotal,
        changeDue,
        customerName: custName
      };

      setPrintModalBill(billModalData);
      setCart([]);
      setCashTendered('');
      setSelectedCustomerId('');
    } catch (err) {
      console.error('Checkout API error:', err);
      // Fallback modal in case of network issue
      setPrintModalBill({
        billNo: 'BILL-' + Date.now().toString().slice(-6),
        date: new Date().toLocaleDateString('en-IN'),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        cart: [...cart],
        subtotal,
        taxAmount,
        grandTotal,
        paymentMode,
        customerName: custName
      });
      setCart([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Top Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '0.85rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShoppingCart size={22} color="#0284c7" />
            <span>{isMr ? 'काऊंटर POS विक्री व बिलिंग' : 'Retail Counter POS Billing'}</span>
          </h2>
          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
            {currentTenant?.name || 'समर्थ कृपा डेअरी'} • {isMr ? 'जलद बारकोड / QR स्कॅन बिलिंग' : 'Fast Barcode / QR Scanning POS'}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            background: gstEnabled ? '#ecfdf5' : '#f1f5f9',
            border: `1px solid ${gstEnabled ? '#a7f3d0' : '#cbd5e1'}`,
            padding: '4px 10px',
            borderRadius: 8,
            fontSize: '0.75rem',
            fontWeight: 700,
            color: gstEnabled ? '#059669' : '#64748b'
          }}>
            {gstEnabled ? (isMr ? 'GST लागू (Tax Mode ON)' : 'GST Enabled') : (isMr ? 'GST बंद (Tax OFF)' : 'GST Disabled')}
          </div>
        </div>
      </div>

      {/* Main Grid: Left Products, Right Cart */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: '1.25rem', alignItems: 'start' }}>
        
        {/* Left: Product Search & Grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: 12, top: 12 }} />
            <input
              type="text"
              placeholder={isMr ? "उत्पादनाचे नाव, कोड किंवा बारकोड स्कॅन करा..." : "Search product name, code or scan barcode..."}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem 1rem 0.6rem 2.4rem',
                borderRadius: 10,
                border: '1px solid #cbd5e1',
                fontSize: '0.85rem',
                boxSizing: 'border-box',
                outline: 'none',
                background: '#ffffff'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.75rem' }}>
            {filteredProducts.map(p => {
              const pName = p.name_mr || p.name || p.name_en;
              const pRate = p.retail_rate !== undefined ? p.retail_rate : p.retailRate;
              const pUnit = p.unit_mr || p.unit || 'नग';

              return (
                <button
                  key={p.id}
                  onClick={() => addToCart(p)}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '0.85rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 105,
                    transition: 'border-color 0.15s, box-shadow 0.15s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#0284c7'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(2,132,199,0.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div>
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#0284c7', background: '#f0f9ff', padding: '1px 6px', borderRadius: 4, display: 'inline-block' }}>
                      {p.code}
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a', marginTop: 4, lineHeight: 1.3 }}>
                      {pName}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                      {p.packing_type || p.packingType || ''}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 6 }}>
                    <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#0f172a' }}>
                      ₹{pRate}
                      <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b' }}>/{pUnit}</span>
                    </div>
                    <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0369a1', fontWeight: 800, padding: '2px 8px', borderRadius: 6 }}>
                      + जोडा
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Cart & Checkout Panel */}
        <div style={{ background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
            <span style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>
              {isMr ? 'काऊंटर बिल पावती' : 'POS Cart'}
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0284c7', background: '#f0f9ff', padding: '2px 8px', borderRadius: 6 }}>
              {cart.length} {isMr ? 'वस्तू' : 'items'}
            </span>
          </div>

          {/* Cart Items List */}
          <div style={{ maxHeight: 250, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#94a3b8', fontSize: '0.82rem' }}>
                {isMr ? 'वस्तू निवडण्यासाठी डावीकडील उत्पादनावर क्लिक करा.' : 'Cart is empty. Click items on the left.'}
              </div>
            ) : (
              cart.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.5rem 0.65rem', borderRadius: 8, border: '1px solid #f1f5f9' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>{item.name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>₹{item.rate} × {item.qty} = <strong>₹{item.rate * item.qty}</strong></div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => updateQty(item.id, -1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Minus size={11} />
                    </button>
                    <span style={{ fontSize: '0.8rem', fontWeight: 800, minWidth: 16, textAlign: 'center' }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, 1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Plus size={11} />
                    </button>
                    <button onClick={() => removeFromCart(item.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: 4 }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Payment Mode Selector */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
              {isMr ? 'पेमेंट प्रकार:' : 'Payment Mode:'}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.4rem' }}>
              {[
                { id: 'CASH', labelMr: 'रोख (Cash)', icon: Banknote },
                { id: 'UPI', labelMr: 'UPI / QR', icon: QrCode },
                { id: 'CREDIT', labelMr: 'खाते / उधारी', icon: CreditCard }
              ].map(mode => {
                const Icon = mode.icon;
                const isSelected = paymentMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    onClick={() => setPaymentMode(mode.id)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 3,
                      padding: '0.45rem 0.2rem',
                      borderRadius: 8,
                      border: isSelected ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                      background: isSelected ? '#f0f9ff' : '#ffffff',
                      color: isSelected ? '#0369a1' : '#475569',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    <Icon size={14} />
                    <span>{mode.labelMr}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Credit Customer Dropdown */}
          {paymentMode === 'CREDIT' && (
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                {isMr ? 'खातेदार ग्राहक निवडा:' : 'Select Customer:'}
              </label>
              <select
                value={selectedCustomerId}
                onChange={e => setSelectedCustomerId(e.target.value)}
                style={{ width: '100%', padding: '0.45rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.8rem', background: '#fff' }}
              >
                <option value="">{isMr ? '-- ग्राहक निवडा --' : '-- Select Customer --'}</option>
                {creditCustomers.map(c => (
                  <option key={c.id} value={c.id.toString()}>{c.name_mr || c.name} ({c.phone})</option>
                ))}
              </select>
            </div>
          )}

          {/* Cash Tendered Input */}
          {paymentMode === 'CASH' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {isMr ? 'दिलेली रोख (₹)' : 'Tendered (₹)'}
                </label>
                <input
                  type="number"
                  placeholder="उदा. 500"
                  value={cashTendered}
                  onChange={e => setCashTendered(e.target.value)}
                  style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#64748b' }}>
                  {isMr ? 'परत देणे (₹)' : 'Change (₹)'}
                </label>
                <div style={{ padding: '0.4rem', fontSize: '0.85rem', fontWeight: 800, color: '#059669' }}>
                  ₹{changeDue}
                </div>
              </div>
            </div>
          )}

          {/* Bill Summary */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.65rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.78rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
              <span>{isMr ? 'एकूण उप-रक्कम (Subtotal):' : 'Subtotal:'}</span>
              <span>₹{subtotal}</span>
            </div>
            {gstEnabled && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                <span>{isMr ? 'GST कर:' : 'GST Tax:'}</span>
                <span>₹{taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '1.15rem', color: '#0f172a', borderTop: '1px dashed #cbd5e1', paddingTop: '0.5rem', marginTop: '0.2rem' }}>
              <span>{isMr ? 'अंतिम देय रक्कम:' : 'Grand Total:'}</span>
              <span style={{ color: '#0284c7' }}>₹{grandTotal}</span>
            </div>
          </div>

          {/* Complete Bill CTA */}
          <button
            disabled={cart.length === 0 || isSubmitting}
            onClick={handleCheckout}
            style={{
              padding: '0.75rem',
              borderRadius: 10,
              border: 'none',
              background: cart.length === 0 ? '#cbd5e1' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              fontWeight: 800,
              fontSize: '0.9rem',
              cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: cart.length > 0 ? '0 4px 10px rgba(2,132,199,0.3)' : 'none'
            }}
          >
            <Printer size={16} />
            <span>{isSubmitting ? (isMr ? 'बिल तयार होत आहे...' : 'Processing...') : (isMr ? 'बिल पूर्ण करा व प्रिंट करा' : 'Complete & Print Receipt')}</span>
          </button>
        </div>

      </div>

      {/* POS Thermal 3" Receipt Print Modal */}
      {printModalBill && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          <div style={{ background: '#ffffff', borderRadius: 14, maxWidth: 360, width: '100%', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: '0.85rem' }}>3" Thermal POS Receipt</span>
              <button onClick={() => setPrintModalBill(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={16} /></button>
            </div>

            <div style={{ padding: '1.25rem', fontFamily: 'monospace', fontSize: '0.75rem', background: '#fff' }}>
              <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '0.5rem' }}>
                <div style={{ fontWeight: 900, fontSize: '1rem' }}>{currentTenant?.brand_name_mr || currentTenant?.name || 'समर्थ कृपा डेअरी'}</div>
                <div>{currentTenant?.dairy_settings?.address || currentTenant?.address || 'महाराष्ट्र'}</div>
                {(currentTenant?.dairy_settings?.print_fssai_gst !== '0') && (
                  <>
                    {currentTenant?.dairy_settings?.gst_no && <div style={{ fontSize: '0.68rem', fontWeight: 700 }}>GSTIN: {currentTenant.dairy_settings.gst_no}</div>}
                    {currentTenant?.dairy_settings?.fssai_no && <div style={{ fontSize: '0.65rem' }}>FSSAI: {currentTenant.dairy_settings.fssai_no}</div>}
                  </>
                )}
                <div style={{ fontSize: '0.65rem' }}>मोबाईल: {currentTenant?.dairy_settings?.phone || currentTenant?.phone || ''}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <span>पावती: {printModalBill.billNo}</span>
                <span>{printModalBill.time}</span>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <div>दिनांक: {printModalBill.date}</div>
                <div>ग्राहक: {printModalBill.customerName}</div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: '1px dashed #000', borderBottom: '1px dashed #000', margin: '0.5rem 0' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '2px 0' }}>आयटम</th>
                    <th style={{ textAlign: 'center' }}>नग</th>
                    <th style={{ textAlign: 'right' }}>दर</th>
                    <th style={{ textAlign: 'right' }}>एकूण</th>
                  </tr>
                </thead>
                <tbody>
                  {printModalBill.cart.map((item, i) => (
                    <tr key={i}>
                      <td style={{ padding: '2px 0' }}>{item.name}</td>
                      <td style={{ textAlign: 'center' }}>{item.qty}</td>
                      <td style={{ textAlign: 'right' }}>{item.rate}</td>
                      <td style={{ textAlign: 'right' }}>{item.rate * item.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, fontSize: '0.95rem', margin: '0.5rem 0' }}>
                <span>एकूण देय:</span>
                <span>₹{printModalBill.grandTotal}</span>
              </div>
              <div style={{ fontSize: '0.7rem' }}>
                पेमेंट प्रकार: {printModalBill.paymentMode}
              </div>

              {currentTenant?.dairy_settings?.print_bank_details === '1' && currentTenant?.dairy_settings?.bank_account_no && (
                <div style={{ borderTop: '1px dashed #000', paddingTop: '0.35rem', marginTop: '0.35rem', fontSize: '0.65rem' }}>
                  <div>बँक: {currentTenant?.dairy_settings?.bank_name || ''}</div>
                  <div>A/C: {currentTenant?.dairy_settings?.bank_account_no} | IFSC: {currentTenant?.dairy_settings?.bank_ifsc || ''}</div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: '1rem', borderTop: '1px dashed #000', paddingTop: '0.5rem', fontSize: '0.7rem' }}>
                ** पुन्हा भेट द्या! धन्यवाद! **
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setPrintModalBill(null)} style={{ flex: 1, padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  {isMr ? 'बंद करा' : 'Close'}
                </button>
                <button onClick={() => window.print()} style={{ flex: 1, padding: '0.5rem', borderRadius: 6, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <Printer size={14} />
                  <span>{isMr ? 'प्रिंट करा' : 'Print'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
